import type { IPFeedbackConfig, ResolvedConfig } from "./types";

const DEFAULTS: Omit<ResolvedConfig, "project"> = {
  jpdProject: "",
  jpdLinkType: "Polaris work item link",
  surveyId: "",
  position: "bottom-right",
  buttonText: "Feedback",
  shakeEnabled: true,
  shakeThreshold: 15,
  captureConsoleErrors: true,
  captureNetworkErrors: true,
  maxConsoleErrors: 20,
  maxNetworkErrors: 10,
  apiUrl: "",
  onSubmitSuccess: () => {},
  onSubmitError: () => {},
};

/**
 * Find the SDK script tag. Uses document.currentScript for sync loading,
 * falls back to searching by src attribute for async/defer loading.
 */
function findScriptTag(): HTMLScriptElement | null {
  if (document.currentScript) return document.currentScript as HTMLScriptElement;

  // Fallback: find the last matching script tag
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[src*="sdk.js"][data-project], script[src*="input.islandpitch.bot"][data-project], script[src*="input-dev.islandpitch.bot"][data-project]',
  );
  return scripts[scripts.length - 1] || null;
}

export function parseScriptAttributes(): Partial<IPFeedbackConfig> {
  const script = findScriptTag();
  if (!script) return {};

  const attrs: Partial<IPFeedbackConfig> = {};

  const project = script.getAttribute("data-project");
  if (project) attrs.project = project;

  const jpdProject = script.getAttribute("data-jpd-project");
  if (jpdProject) attrs.jpdProject = jpdProject;

  const jpdLinkType = script.getAttribute("data-jpd-link-type");
  if (jpdLinkType) attrs.jpdLinkType = jpdLinkType;

  const surveyId = script.getAttribute("data-survey-id");
  if (surveyId) attrs.surveyId = surveyId;

  const position = script.getAttribute("data-position");
  if (position) attrs.position = position as IPFeedbackConfig["position"];

  const buttonText = script.getAttribute("data-button-text");
  if (buttonText) attrs.buttonText = buttonText;

  const shakeEnabled = script.getAttribute("data-shake-enabled");
  if (shakeEnabled !== null) attrs.shakeEnabled = shakeEnabled !== "false";

  const shakeThreshold = script.getAttribute("data-shake-threshold");
  if (shakeThreshold) attrs.shakeThreshold = parseFloat(shakeThreshold);

  // Derive API URL from script src
  if (!attrs.apiUrl) {
    try {
      const url = new URL(script.src);
      attrs.apiUrl = url.origin;
    } catch {
      // ignore
    }
  }

  return attrs;
}

/**
 * Resolve config with defaults.
 */
export function resolveConfig(partial: Partial<IPFeedbackConfig>): ResolvedConfig {
  if (!partial.project) {
    throw new Error("IPFeedback: 'project' is required (Jira project key)");
  }

  return {
    ...DEFAULTS,
    ...partial,
    project: partial.project,
  } as ResolvedConfig;
}
